import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { FileItem } from "@/types/types";

export const capitalize = (s: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

export const removeDiacritics = (s: string) =>
  (s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export const hasDiacritics = (s: string) =>
  /[\u0300-\u036f]/.test((s ?? "").normalize("NFD"));

export const fold = (s: string, strip: boolean) =>
  (strip ? removeDiacritics(s) : s ?? "").toLowerCase().trim();

export const normalizeLanguageCode = (lang?: string, fallback = "") => {
  const raw = (lang ?? "").replace("_", "-").toLowerCase().trim();
  if (!raw) return fallback;
  if (raw.startsWith("es")) return "es-ES";
  if (raw.startsWith("en")) return "en-US";
  if (raw.startsWith("ca")) return "ca-ES";
  if (raw.startsWith("gl") || raw.startsWith("ga")) return "ga-ES";
  if (raw.startsWith("eu")) return "eu-ES";
  return raw;
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const parseNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;

  const normalized =
    typeof value === "string"
      ? value.replace("%", "").replace(",", ".").trim()
      : value;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export const toNum = (v: unknown) => parseNumber(v) ?? 0;

export const toNumber = (value: string | number | null | undefined) =>
  Number(value);

export const bySort = <T extends { sort?: number | null }>(items: T[]) =>
  [...items].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

export const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export const fmt = (n: number) =>
  new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(n);

export const getFolderIdAndFiles = (
  entity:
    | {
        folder?: Array<
          number | { id?: number | string; files?: FileItem[] }
        >;
      }
    | null
    | undefined
): { folderId?: number; files: FileItem[] } => {
  const firstFolder = entity?.folder?.[0];
  const folderId =
    typeof firstFolder === "number" ? firstFolder : firstFolder?.id;
  const files = typeof firstFolder === "number" ? [] : firstFolder?.files ?? [];

  return {
    folderId: folderId != null ? Number(folderId) : undefined,
    files,
  };
};
